// src/Components/HostControlPanel.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import gameControlService from "../Services/gameControlService";
import LeaveGameButton from "./LeaveGameButton";

const HostControlPanel = ({ roomData, roomDocId, currentPhase }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    isProcessing: false,
  });

  const navigate = useNavigate();
  const currentUserId = localStorage.getItem("currentUserId");
  const currentRoomId = localStorage.getItem("currentRoomId");

  // Handle player removal
  const handleRemovePlayer = (playerId, playerName) => {
    setConfirmationModal({
      isOpen: true,
      title: "Remove Player",
      message: `Remove ${playerName} from the game? They won't be able to rejoin this session.`,
      onConfirm: async () => {
        setConfirmationModal(prev => ({ ...prev, isProcessing: true }));
        
        const result = await gameControlService.removePlayer(
          currentRoomId,
          playerId,
          roomData,
          roomDocId
        );

        if (!result.success) {
          alert(result.message || "Failed to remove player");
        }

        setConfirmationModal({ isOpen: false, title: "", message: "", onConfirm: () => {}, isProcessing: false });
      },
      isProcessing: false,
    });
  };

  // Handle end game
  const handleEndGame = () => {
    setConfirmationModal({
      isOpen: true,
      title: "End Game",
      message: "End the game now? All players will see final results and the game will be over.",
      onConfirm: async () => {
        setConfirmationModal(prev => ({ ...prev, isProcessing: true }));
        
        const result = await gameControlService.endGameForAll(
          roomDocId,
          currentUserId,
          "host_ended"
        );

        if (result.success) {
          // The game coordinator will handle the UI update
          setConfirmationModal({ isOpen: false, title: "", message: "", onConfirm: () => {}, isProcessing: false });
        } else {
          alert(result.message || "Failed to end game");
          setConfirmationModal({ isOpen: false, title: "", message: "", onConfirm: () => {}, isProcessing: false });
        }
      },
      isProcessing: false,
    });
  };

  // Handle delete room
  const handleDeleteRoom = () => {
    setConfirmationModal({
      isOpen: true,
      title: "Delete Room",
      message: "Delete this room completely? This will end the game for all players and cannot be undone.",
      onConfirm: async () => {
        setConfirmationModal(prev => ({ ...prev, isProcessing: true }));
        
        const result = await gameControlService.deleteRoom(currentRoomId);

        if (result.success) {
          navigate("/");
        } else {
          alert(result.message || "Failed to delete room");
          setConfirmationModal({ isOpen: false, title: "", message: "", onConfirm: () => {}, isProcessing: false });
        }
      },
      isProcessing: false,
    });
  };

  // Confirmation Modal Component
  const ConfirmationModal = () => {
    if (!confirmationModal.isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="bg-orange-50 border-b border-orange-200 px-6 py-4 flex items-center gap-3">
            <span className="text-2xl">⚠️</span>
            <h2 className="text-lg font-bold text-orange-900">
              {confirmationModal.title}
            </h2>
          </div>
          <div className="p-6">
            <p className="text-gray-700 mb-6">{confirmationModal.message}</p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmationModal({ isOpen: false, title: "", message: "", onConfirm: () => {}, isProcessing: false })}
                disabled={confirmationModal.isProcessing}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-100 transition duration-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmationModal.onConfirm}
                disabled={confirmationModal.isProcessing}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {confirmationModal.isProcessing ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!roomData || roomData.hostId !== currentUserId) {
    return null; // Don't show for non-hosts
  }

  return (
    <>
      {/* Always renders just the trigger button inline in GameFlow.jsx's
          header row — the panel itself is a real modal now (see below),
          so it can never push or crowd that header no matter how tall its
          content gets. */}
      <button
        onClick={() => setIsExpanded(true)}
        className="bg-red-600 text-white px-3 py-2 rounded-lg shadow-lg hover:bg-red-700 transition-all duration-300 flex items-center space-x-2"
        title="Host Controls"
      >
        <span className="text-sm font-semibold">Settings</span>
      </button>

      {isExpanded && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[55] flex items-center justify-center p-4"
          onClick={() => setIsExpanded(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl border-2 border-red-200 w-full max-w-xs overflow-hidden max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center bg-red-50 px-4 py-3 border-b border-red-100 shrink-0">
              <h3 className="text-base font-bold text-red-800 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                    clipRule="evenodd"
                  />
                </svg>
                Host Controls
              </h3>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-gray-500 hover:text-gray-700 p-1"
                title="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-4 divide-y divide-gray-100 overflow-y-auto">
            {/* Players Section */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-2 text-sm">
                Players ({roomData.players?.length || 0}/{roomData.maxPlayers || 3})
              </h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {roomData.players?.map((player) => (
                  <div
                    key={player.id}
                    className="flex items-center justify-between py-1.5 px-2 rounded text-sm"
                  >
                    <span className="truncate mr-2">
                      {player.name}
                      {player.isHost && (
                        <span className="text-red-600 text-xs ml-1">(Host)</span>
                      )}
                    </span>
                    {player.id !== currentUserId && (
                      <button
                        onClick={() => handleRemovePlayer(player.id, player.name)}
                        className="text-red-600 hover:text-red-800 text-xs p-1"
                        title="Remove Player"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Game Controls Section */}
            <div className="pt-4 space-y-2">
              <h4 className="font-semibold text-gray-800 mb-2 text-sm">Game Controls</h4>

              <button
                onClick={handleEndGame}
                className="w-full bg-orange-600 text-white py-2 px-3 rounded-lg hover:bg-orange-700 transition duration-300 text-sm font-medium"
              >
                End Game Now
              </button>

              {/* <button
                onClick={handleDeleteRoom}
                className="w-full bg-red-600 text-white py-2 px-3 rounded-lg hover:bg-red-700 transition duration-300 text-sm font-medium"
              >
                Delete Room
              </button> */}

              {/* Reuses the same component (and its own confirmation +
                  leave logic) non-hosts get in the top bar, so there's one
                  leave flow, not two — this just relocates its entry point
                  for hosts, who now find every control behind Settings.
                  The wrapper only stretches its button to match the other
                  full-width controls here; LeaveGameButton itself is
                  unchanged for its inline (non-host, top-bar) usage. */}
              <div className="[&>button]:w-full">
                <LeaveGameButton roomData={roomData} roomDocId={roomDocId} />
              </div>
            </div>

            {/* Current Phase Info */}
            <div className="pt-3">
              <div className="text-xs text-gray-600">
                <div>Current Phase: <span className="font-medium capitalize">{currentPhase}</span></div>
                <div>Round: <span className="font-medium">{roomData.currentRound || 0}</span></div>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal />
    </>
  );
};

export default HostControlPanel;